import { requireTaskType } from "../../tasks/taskType";
import { normalizeInstallation } from "../../tasks/installation";
import { requireTaskSchedule } from "../../tasks/requireTaskSchedule";
import type {
  AdjustmentInput,
  BudgetAdjustment,
} from "../../finance/adjustmentModel";
import type {
  AnalyticsFilter,
  AnalyticsData,
} from "../../finance/analyticsModel";
import { projectLookupFilter } from "../commons/projectLookupFilter";
import type {
  StandaloneExpenseInput,
  ExpenseReviewInput,
  ExpenseReview,
} from "../../finance/expenseModel";
import type { FinanceInput, FinancePlan } from "../../finance/financeModel";
import { supabaseDataProvider } from "ra-supabase-core";
import {
  withLifecycleCallbacks,
  type DataProvider,
  type GetListParams,
  type Identifier,
  type ResourceCallbacks,
} from "ra-core";
import type {
  ContactNote,
  TaskComment,
  Expense,
  Deal,
  DealNote,
  RAFile,
  Sale,
  SalesFormData,
  SignUpData,
} from "../../types";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import { ATTACHMENTS_BUCKET } from "../commons/attachments";
import { getIsInitialized } from "./authProvider";
import { getSupabaseClient } from "./supabase";
import { signStoredRecord } from "./privateFiles";
import { crmHttpClient } from "./httpClient";

const SALES_COLUMNS =
  "id,first_name,last_name,email,secondary_emails,user_id,administrator,hidden_from_team,role,disabled,position,department,phone,deleted_at";

const getBaseDataProvider = () =>
  supabaseDataProvider({
    instanceUrl: import.meta.env.VITE_SUPABASE_URL,
    apiKey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
    supabaseClient: getSupabaseClient(),
    httpClient: crmHttpClient,
    sortOrder: "asc,desc.nullslast" as any,
  });

const processCompanyLogo = async (params: any) => {
  const logo = params.data.logo;

  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

const getDataProviderWithCustomMethods = () => {
  const baseDataProvider = getBaseDataProvider();

  return {
    ...baseDataProvider,
    async adjustProjectBudget(
      input: AdjustmentInput,
    ): Promise<BudgetAdjustment> {
      const { data, error } = await getSupabaseClient().rpc(
        "adjust_project_budget",
        {
          p_deal_id: input.deal_id,
          p_expected_plan_revision: input.expected_plan_revision,
          p_expected_revision: input.expected_revision,
          p_from_zone: input.from_zone,
          p_to_zone: input.to_zone,
          p_amount: input.amount,
          p_reason: input.reason,
          p_note: input.note,
          p_sales_id: input.sales_id,
          p_company_id: input.company_id,
          p_reverses_id: input.reverses_id,
        },
      );
      if (error) throw error;
      return data as BudgetAdjustment;
    },
    async getFinancialAnalytics(
      filter: AnalyticsFilter,
    ): Promise<AnalyticsData> {
      const { data, error } = await getSupabaseClient().rpc(
        "get_financial_analytics",
        { p_start: filter.start, p_end: filter.end, p_deal_id: filter.deal_id },
      );
      if (error) throw error;
      return data as AnalyticsData;
    },
    async createProjectExpense(
      input: StandaloneExpenseInput,
    ): Promise<ExpenseReview> {
      const { data, error } = await getSupabaseClient().rpc(
        "create_project_expense",
        {
          p_deal_id: input.deal_id,
          p_amount: input.amount,
          p_category: input.category,
          p_spent_at: input.spent_at,
          p_note: input.note,
          p_zone_key: input.zone_key,
          p_reason: input.reason,
          p_to_zone: input.to_zone,
          p_beneficiary_sales_id: input.beneficiary_sales_id,
          p_submission_key: input.submission_key,
        },
      );
      if (error) throw error;
      return data as ExpenseReview;
    },
    async reviewProjectExpense(
      input: ExpenseReviewInput,
    ): Promise<ExpenseReview> {
      const { data, error } = await getSupabaseClient().rpc(
        "review_project_expense",
        {
          p_expense_id: input.expense_id,
          p_deal_id: input.deal_id,
          p_expected_revision: input.expected_revision,
          p_decision: input.decision,
          p_zone_key: input.zone_key,
          p_reason: input.reason,
          p_note: input.note,
          p_to_zone: input.to_zone ?? "reserve",
        },
      );
      if (error) throw error;
      return data as ExpenseReview;
    },
    async saveProjectFinance(input: FinanceInput): Promise<FinancePlan> {
      const { data, error } = await getSupabaseClient().rpc(
        "save_project_finance",
        {
          p_deal_id: input.deal_id,
          p_expected_revision: input.expected_revision,
          p_zones: input.zones,
          p_reason: input.reason,
        },
      );
      if (error) throw error;
      return data as FinancePlan;
    },
    async getList(resource: string, params: GetListParams) {
      if (
        resource === "deals" &&
        ["register_client", "register_owner"].includes(params.sort?.field ?? "")
      ) {
        const result = await baseDataProvider.getList("deal_register", params);
        return {
          ...result,
          data: result.data.map((row) => {
            const record = { ...row };
            delete record.register_client;
            delete record.register_owner;
            return record;
          }),
        };
      }
      if (resource === "sales") {
        return baseDataProvider.getList(resource, {
          ...params,
          filter: { ...params.filter, "deleted_at@is": null },
          meta: { ...params.meta, columns: SALES_COLUMNS },
        });
      }
      if (resource === "project_lookup") {
        return baseDataProvider.getList(
          params.meta?.includeArchived
            ? "project_lookup"
            : "project_lookup_active",
          {
            ...params,
            filter: projectLookupFilter(params.filter),
          },
        );
      }
      if (resource === "companies") {
        return baseDataProvider.getList("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getList("contacts_summary", params);
      }
      if (resource === "activity_log") {
        const { data, total } = await baseDataProvider.getList(
          "activity_log",
          params,
        );
        // Rename snake_case view columns to camelCase to match Activity type
        return {
          data: data.map((row: any) => ({
            ...row,

            contactNote: row.contact_note ?? undefined,

            dealNote: row.deal_note ?? undefined,

            taskActivity: row.task_activity ?? undefined,

            contact_note: undefined,
            deal_note: undefined,
            task_activity: undefined,
          })),
          total,
        };
      }

      return baseDataProvider.getList(resource, params);
    },
    async getOne(resource: string, params: any) {
      if (resource === "companies") {
        return baseDataProvider.getOne("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getOne("contacts_summary", params);
      }

      return baseDataProvider.getOne(resource, params);
    },

    async getMany(resource: string, params: any) {
      return baseDataProvider.getMany(
        resource,
        resource === "sales"
          ? {
              ...params,
              meta: { ...params.meta, columns: SALES_COLUMNS },
            }
          : params,
      );
    },

    async delete(resource: string, params: any) {
      if (resource === "tasks") {
        const { data, error } = await getSupabaseClient()
          .from("tasks")
          .delete()
          .eq("id", params.id)
          .select("id")
          .maybeSingle();
        if (error) throw new Error(error.message);
        if (!data)
          throw new Error(
            "Задача не удалена: нет доступа или запись уже недоступна. Обновите календарь.",
          );
        return { data: { ...params.previousData, id: data.id } };
      }
      if (resource === "deals") {
        const { data, error } = await getSupabaseClient().rpc(
          "delete_unused_deal",
          {
            p_deal_id: params.id,
          },
        );
        if (error) throw new Error(error.message);
        return { data };
      }
      if (resource !== "sales")
        return baseDataProvider.delete(resource, params);
      const { data, error } = await getSupabaseClient().functions.invoke(
        "users",
        {
          method: "DELETE",
          body: { sales_id: params.id },
        },
      );
      if (error || !data?.data) {
        let details: any = {};
        try {
          details = (await error?.context?.json()) ?? {};
        } catch {
          /* Network failure */
        }
        throw new Error(
          details.message ??
            "Не удалось удалить пользователя. Обновите список и проверьте результат.",
        );
      }
      return data;
    },

    async deleteMany(resource: string, params: any) {
      if (resource !== "sales")
        return baseDataProvider.deleteMany(resource, params);
      const deleted = [];
      for (const id of params.ids) {
        const { data, error } = await getSupabaseClient().functions.invoke(
          "users",
          {
            method: "DELETE",
            body: { sales_id: id },
          },
        );
        if (error || !data?.data) {
          let details: any = {};
          try {
            details = (await error?.context?.json()) ?? {};
          } catch {
            /* Network failure */
          }
          throw new Error(
            details.message ??
              "Не все пользователи удалены. Обновите список перед повторением.",
          );
        }
        deleted.push(id);
      }
      return { data: deleted };
    },

    async resendInvitation(id: Identifier) {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "users",
        {
          method: "POST",
          body: { action: "resend_invitation", sales_id: id },
        },
      );
      if (error || !data?.data) {
        let details: any = {};
        try {
          details = (await error?.context?.json()) ?? {};
        } catch {
          /* Network failure */
        }
        throw new Error(details.message ?? "Не удалось повторить приглашение");
      }
      return data.data;
    },

    async signUp(
      _data: SignUpData,
    ): Promise<{ id: string; email: string; password: string }> {
      throw new Error("Доступ только по приглашению администратора");
    },
    async salesCreate(body: SalesFormData) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        data: Sale;
      }>("users", {
        method: "POST",
        body,
      });

      if (!data || error) {
        console.error("salesCreate.error", error);
        const errorDetails = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();
        throw Object.assign(
          new Error(errorDetails?.message || "Failed to create the user"),
          { code: errorDetails?.code, email: errorDetails?.email },
        );
      }

      return data.data;
    },
    async salesUpdate(
      id: Identifier,
      data: Partial<Omit<SalesFormData, "password">>,
    ) {
      const {
        email,
        secondary_emails,
        first_name,
        last_name,
        administrator,
        hidden_from_team,
        role,
        avatar,
        disabled,
        phone,
        position,
        department,
      } = data;

      let storedAvatar: any = avatar;
      if (avatar && typeof avatar === "object" && "src" in avatar) {
        await uploadToBucket(avatar as RAFile);
        const { rawFile: _file, ...saved } = avatar as RAFile;
        storedAvatar = saved;
      }

      const { data: updatedData, error } =
        await getSupabaseClient().functions.invoke<{
          data: Sale;
        }>("users", {
          method: "PATCH",
          body: {
            sales_id: id,
            email,
            secondary_emails,
            first_name,
            last_name,
            administrator,
            hidden_from_team,
            role,
            disabled,
            avatar: storedAvatar,

            phone,
            position,
            department,
          },
        });

      if (!updatedData || error) {
        console.error("salesUpdate.error", error);

        const errorDetails = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();

        throw Object.assign(
          new Error(
            errorDetails?.message || "Failed to update account manager",
          ),
          {
            code: errorDetails?.code,
            email: errorDetails?.email,
          },
        );
      }

      return updatedData.data;
    },
    async updatePassword(id: Identifier) {
      const { data: passwordUpdated, error } =
        await getSupabaseClient().functions.invoke<boolean>("update_password", {
          method: "PATCH",
          body: {
            sales_id: id,
          },
        });

      if (!passwordUpdated || error) {
        console.error("update_password.error", error);
        throw new Error("Failed to update password");
      }

      return passwordUpdated;
    },
    async unarchiveDeal(deal: Deal) {
      // get all deals where stage is the same as the deal to unarchive
      const { data: deals } = await baseDataProvider.getList<Deal>("deals", {
        filter: { stage: deal.stage },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "index", order: "ASC" },
      });

      // set index for each deal starting from 1, if the deal to unarchive is found, set its index to the last one
      const updatedDeals = deals.map((d, index) => ({
        ...d,
        index: d.id === deal.id ? 0 : index + 1,
        archived_at: d.id === deal.id ? null : d.archived_at,
      }));

      return await Promise.all(
        updatedDeals.map((updatedDeal) =>
          baseDataProvider.update("deals", {
            id: updatedDeal.id,
            data: updatedDeal,
            previousData: deals.find((d) => d.id === updatedDeal.id),
          }),
        ),
      );
    },
    async isInitialized() {
      return getIsInitialized();
    },
    async mergeContacts(sourceId: Identifier, targetId: Identifier) {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "merge_contacts",
        {
          method: "POST",
          body: { loserId: sourceId, winnerId: targetId },
        },
      );

      if (error) {
        console.error("merge_contacts.error", error);
        throw new Error("Failed to merge contacts");
      }

      return data;
    },
    async getConfiguration(): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.getOne("configuration", {
        id: 1,
      });
      return signStoredRecord(
        (data?.config as ConfigurationContextValue) ?? {},
      );
    },
    async updateConfiguration(
      config: ConfigurationContextValue,
    ): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.update("configuration", {
        id: 1,
        data: { config },
        previousData: { id: 1 },
      });
      return data.config as ConfigurationContextValue;
    },
  } satisfies DataProvider;
};

export type CrmDataProvider = ReturnType<
  typeof getDataProviderWithCustomMethods
>;

const processConfigLogo = async (logo: any): Promise<string> => {
  if (typeof logo === "string") return logo;
  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
    return logo.src;
  }
  return logo?.src ?? "";
};

const lifeCycleCallbacks: ResourceCallbacks[] = [
  {
    resource: "tasks",
    beforeCreate: async (params) => ({
      ...params,
      data: requireTaskSchedule(
        requireTaskType(normalizeInstallation(params.data)),
      ),
    }),
    beforeUpdate: async (params) => ({
      ...params,
      data:
        params.previousData || params.data.type
          ? requireTaskSchedule(
              requireTaskType(
                normalizeInstallation({
                  ...params.previousData,
                  ...params.data,
                }),
                params.previousData,
              ),
              params.previousData,
            )
          : params.data,
    }),
  },
  {
    resource: "configuration",
    beforeUpdate: async (params) => {
      const config = params.data.config;
      if (config) {
        config.lightModeLogo = await processConfigLogo(config.lightModeLogo);
        config.darkModeLogo = await processConfigLogo(config.darkModeLogo);
      }
      return params;
    },
  },
  {
    resource: "expense_reviews",
  },
  {
    resource: "expenses",
    beforeSave: async (data: Expense) => {
      if (data.attachments?.length)
        data.attachments = await Promise.all(
          data.attachments.map((file) =>
            uploadToBucket(file, `task-expenses/${data.task_id}/`),
          ),
        );
      return data;
    },
  },
  {
    resource: "task_comments",
    beforeSave: async (data: TaskComment) => {
      if (data.attachments?.length)
        data.attachments = await Promise.all(
          data.attachments.map((file) =>
            uploadToBucket(file, `task-comments/${data.task_id}/`),
          ),
        );
      return data;
    },
  },
  {
    resource: "contact_notes",
    beforeSave: async (data: ContactNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "deal_notes",
    beforeSave: async (data: DealNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "sales",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["first_name", "last_name"])(params);
    },
    beforeSave: async (data: Sale, _, __) => {
      if (data.avatar) {
        await uploadToBucket(data.avatar);
      }
      return data;
    },
  },
  {
    resource: "contacts",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "first_name",
        "last_name",
        "company_name",
        "title",
        "email",
        "phone",
        "background",
      ])(params);
    },
  },
  {
    resource: "companies",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "name",
        "phone_number",
        "website",
        "zipcode",
        "city",
        "state_abbr",
      ])(params);
    },
    beforeCreate: async (params) => {
      const createParams = await processCompanyLogo(params);

      return {
        ...createParams,
        data: {
          created_at: new Date().toISOString(),
          ...createParams.data,
        },
      };
    },
    beforeUpdate: async (params) => {
      return await processCompanyLogo(params);
    },
  },
  {
    resource: "contacts_summary",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["first_name", "last_name"])(params);
    },
  },
  {
    resource: "project_lookup",
    beforeGetList: async (params) => {
      const { q, ...filter } = params.filter ?? {};
      return applyFullTextSearch(["name"])({
        ...params,
        filter: q ? { ...filter, q } : filter,
      });
    },
  },
  {
    resource: "deals",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "category", "description"])(params);
    },
  },
];

export const getDataProvider = () => {
  if (import.meta.env.VITE_SUPABASE_URL === undefined) {
    throw new Error("Please set the VITE_SUPABASE_URL environment variable");
  }
  if (import.meta.env.VITE_SB_PUBLISHABLE_KEY === undefined) {
    throw new Error(
      "Please set the VITE_SB_PUBLISHABLE_KEY environment variable",
    );
  }
  return withLifecycleCallbacks(
    getDataProviderWithCustomMethods(),
    lifeCycleCallbacks.map((callbacks) => ({
      ...callbacks,
      afterRead: signStoredRecord,
    })),
  ) as CrmDataProvider;
};

const applyFullTextSearch = (columns: string[]) => (params: GetListParams) => {
  if (!params.filter?.q) {
    return params;
  }
  const { q, ...filter } = params.filter;
  return {
    ...params,
    filter: {
      ...filter,
      "@or": columns.reduce((acc, column) => {
        if (column === "email")
          return {
            ...acc,
            [`email_fts@ilike`]: q,
          };
        if (column === "phone")
          return {
            ...acc,
            [`phone_fts@ilike`]: q,
          };
        else
          return {
            ...acc,
            [`${column}@ilike`]: q,
          };
      }, {}),
    },
  };
};

const uploadToBucket = async (fi: RAFile, prefix = "") => {
  if (!fi.src.startsWith("blob:") && !fi.src.startsWith("data:")) {
    // Sign URL check if path exists in the bucket
    if (fi.path) {
      const { error } = await getSupabaseClient()
        .storage.from(ATTACHMENTS_BUCKET)
        .createSignedUrl(fi.path, 60);

      if (!error) {
        return fi;
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src)
        .then((res) => {
          if (res.status !== 200) {
            return null;
          }
          return res.blob();
        })
        .catch(() => null)
    : fi.rawFile;

  if (dataContent == null) {
    // We weren't able to download the file from its src (e.g. user must be signed in on another website to access it)
    // or the file has no content (not probable)
    // In that case, just return it as is: when trying to download it, users should be redirected to the other website
    // and see they need to be signed in. It will then be their responsibility to upload the file back to the note.
    return fi;
  }

  const file = fi.rawFile;
  const originalName = file?.name ?? fi.title ?? "attachment";
  const fileParts = originalName.split(".");
  const fileExt = fileParts.length > 1 ? `.${fileParts.pop()}` : "";
  const fileName = `${crypto.randomUUID()}${fileExt}`;
  const filePath = `${prefix}${fileName}`;
  const { error: uploadError } = await getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .upload(filePath, dataContent);

  if (uploadError) {
    console.error("uploadError", uploadError);
    throw new Error("Failed to upload attachment");
  }

  const { data } = getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .getPublicUrl(filePath);

  fi.path = filePath;
  fi.src = data.publicUrl;

  // save MIME type
  const mimeType = dataContent.type || file?.type;
  fi.type = mimeType;

  return fi;
};
